import React, { useState, useEffect } from 'react';
import { useParams, useNavigate }    from 'react-router-dom';
import Navbar                        from '../Navbar/Navbar';
import api                           from '../Axios/axios';
import { FaPlus, FaEdit, FaTrash }   from 'react-icons/fa';
import './RentAccountPage.scss';

export default function RentAccountPage() {
  const { companyId } = useParams();
  const navigate     = useNavigate();

  // Company & report
  const [company, setCompany] = useState(null);
  const [report,  setReport]  = useState(null);
  const [loading, setLoading]= useState(true);

  // Payments
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    DeliveryDate:        '',
    ChequeOrBondDate:    '',
    ChequeOrBondNumber:  '',
    PaymentType:         'Cheque',
    Amount:              '',
    InvoiceAmount:       '',
    IsInvoiceIssued:     false,
    InvoiceDate:         '',
    InvoiceNumber:       '',
    ConstructionPayment: false,
    Note:                '',
    Files:               []
  });

  // Services
  const [showServiceModal,       setShowServiceModal]       = useState(false);
  const [editingServiceId,       setEditingServiceId]       = useState(null);
  const [currentServiceRentalId, setCurrentServiceRentalId] = useState(null);
  const [serviceForm,            setServiceForm]            = useState({
    Description: '',
    ServiceDate: '',
    Amount:      '',
    Note:        '',
    Files:       []
  });
  const [servicesByRental, setServicesByRental] = useState({});

  // format: ay + gün
  const formatDuration = (m0,m1,days) => {
    const carry = Math.floor((days||0)/30);
    const d     = (days||0)%30;
    const tot   = (m0||0)+(m1||0)+carry;
    return `${tot} ay${d>0?` ${d} gün`:''}`;
  };

  // Fetch company & report
  useEffect(() => {
    async function fetchData(){
      try {
        const compRes = await api.get(`/Company/GetById?id=${companyId}`);
        const comp    = compRes.data.data;
        setCompany(comp);

        if(comp.rentTowerCrane){
          const repRes = await api.get(
            `/Rental/Report/${companyId}?rentTowerCrane=true`
          );
          setReport(repRes.data.data);
        }
      } catch(err){
        console.error('Veri çekme hatası:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  },[companyId]);

  // When rentals arrive, fetch their services
  useEffect(() => {
    if(!report?.rentals) return;
    report.rentals.forEach(r=>{
      if(r.rentalId != null) fetchServices(r.rentalId);
    });
  },[report]);

  const fetchServices = async rentalId => {
    try {
      const res = await api.get(`/Service/${rentalId}`);
      setServicesByRental(prev=>({
        ...prev,
        [rentalId]: res.data.data
      }));
    } catch(err){
      console.error('Servisleri yüklerken hata:', err);
    }
  };

  //
  // —— PAYMENT MODAL
  //

  const openPaymentModal = payment => {
    if(payment){
      setPaymentForm({
        DeliveryDate:        payment.deliveryDate?.split('T')[0]    || '',
        ChequeOrBondDate:    payment.chequeOrBondDate?.split('T')[0]|| '',
        ChequeOrBondNumber:  payment.chequeOrBondNumber             || '',
        PaymentType:         payment.paymentType,
        Amount:              payment.amount,
        InvoiceAmount:       payment.invoiceAmount,
        IsInvoiceIssued:     payment.isInvoiceIssued,
        InvoiceDate:         payment.invoiceDate?.split('T')[0]     || '',
        InvoiceNumber:       payment.invoiceNumber,
        ConstructionPayment: payment.constructionPayment,
        Note:                payment.note,
        Files:               []
      });
      setEditingPaymentId(payment.id);
    } else {
      setPaymentForm({
        DeliveryDate:        '',
        ChequeOrBondDate:    '',
        ChequeOrBondNumber:  '',
        PaymentType:         'Cheque',
        Amount:              '',
        InvoiceAmount:       '',
        IsInvoiceIssued:     false,
        InvoiceDate:         '',
        InvoiceNumber:       '',
        ConstructionPayment: false,
        Note:                '',
        Files:               []
      });
      setEditingPaymentId(null);
    }
    setShowPaymentModal(true);
  };
  const closePaymentModal = () => setShowPaymentModal(false);

  const handlePaymentChange = e => {
    const {name, value, type, checked} = e.target;
    setPaymentForm(f=>({
      ...f,
      [name]: type==='checkbox' ? checked : value
    }));
  };
  const handlePaymentFile = e=>
    setPaymentForm(f=>({ ...f, Files: Array.from(e.target.files) }));

  const submitPayment = async ()=>{
    const fd = new FormData();
    fd.append('CompanyId', companyId);
    if(editingPaymentId) fd.append('Id', editingPaymentId);

    // append all fields
    [
      'DeliveryDate','ChequeOrBondDate','ChequeOrBondNumber',
      'PaymentType','Amount','InvoiceAmount','IsInvoiceIssued',
      'InvoiceDate','InvoiceNumber','ConstructionPayment','Note'
    ].forEach(k=>fd.append(k, paymentForm[k]));

    paymentForm.Files.forEach((file,i)=>fd.append(`Files[${i}]`, file));

    try{
      if(editingPaymentId){
        await api.put('/Payment', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
      } else {
        await api.post('/Payment', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
      }
      // refresh both report and services totals
      const repRes = await api.get(`/Rental/Report/${companyId}?rentTowerCrane=true`);
      setReport(repRes.data.data);
      setShowPaymentModal(false);
    } catch(err){
      console.error('Ödeme kaydetme hatası:', err);
    }
  };

  const deletePayment = async id=>{
    if(!window.confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) return;
    try{
      await api.delete('/Payment',{ data:{ Id:id }});
      const repRes = await api.get(`/Rental/Report/${companyId}?rentTowerCrane=true`);
      setReport(repRes.data.data);
    } catch(err){
      console.error('Ödeme silme hatası:', err);
    }
  };

  //
  // —— SERVICE MODAL
  //

  const openServiceModal = (rentalId, service)=>{
    setCurrentServiceRentalId(rentalId);
    if(service){
      setServiceForm({
        Description: service.description,
        ServiceDate: service.serviceDate?.split('T')[0]|| '',
        Amount:      service.amount,
        Note:        service.note,
        Files:       []
      });
      setEditingServiceId(service.id);
    } else {
      setServiceForm({ Description:'', ServiceDate:'', Amount:'', Note:'', Files:[] });
      setEditingServiceId(null);
    }
    setShowServiceModal(true);
  };
  const closeServiceModal = ()=>setShowServiceModal(false);

  const handleServiceChange = e=>{
    const {name,value} = e.target;
    setServiceForm(f=>({...f,[name]:value}));
  };
  const handleServiceFile = e=>
    setServiceForm(f=>({...f,Files:Array.from(e.target.files)}));

  const submitService = async ()=>{
    const fd = new FormData();
    // Always send RentalId (required), and Id when editing
    fd.append('RentalId', currentServiceRentalId);
    if (editingServiceId) {
      fd.append('Id', editingServiceId);
    }

    ['Description','ServiceDate','Amount','Note']
      .forEach(k=>fd.append(k, serviceForm[k]));
    serviceForm.Files.forEach((file,i)=>fd.append(`Files[${i}]`, file));

    try{
      if(editingServiceId){
        await api.put('/Service', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
      } else {
        await api.post('/Service', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
      }
      // re-fetch both service list and totals
      await fetchServices(currentServiceRentalId);
      const repRes = await api.get(`/Rental/Report/${companyId}?rentTowerCrane=true`);
      setReport(repRes.data.data);
      setShowServiceModal(false);
    } catch(err){
      console.error('Servis kaydetme hatası:', err);
    }
  };

  const deleteService = async id=>{
    if(!window.confirm('Bu servis kaydını silmek istediğinize emin misiniz?')) return;
    try{
      await api.delete('/Service',{ data:{ Id:id }});
      await fetchServices(currentServiceRentalId);
      const repRes = await api.get(`/Rental/Report/${companyId}?rentTowerCrane=true`);
      setReport(repRes.data.data);
    } catch(err){
      console.error('Servis silme hatası:', err);
    }
  };

  if(loading){
    return (
      <div className="account-page">
        <Navbar/>
        <div className="content"><p>Yükleniyor…</p></div>
      </div>
    );
  }
  if(company && !company.rentTowerCrane){
    return (
      <div className="account-page">
        <Navbar/>
        <div className="content"><p>Bu firma kule vinç kiralamıyor.</p></div>
      </div>
    );
  }
  if(!report) return null;

  // only construction payments
  const constructionPayments = (report.payments || [])
    .filter(p=>p.constructionPayment);

  return (
    <div className="account-page">
      <Navbar/>

      {/* HEADER */}
      <header className="account-header">
        <h1>{company.name}</h1>
        <p className="subtitle">Kiralayan Firma Muhasebesi</p>
      </header>

      {/* TOTALS */}
      <div className="totals">
        <div className="total-card progress">
          <span className="label">Toplam Sözleşme ve Servis Bedeli </span>
          <span className="value">
            {(((report.totalContractPrice||0)
             + (report.totalServiceAmount||0))
             .toLocaleString())} TL
          </span>
        </div>
        <div className="total-card paid">
          <span className="label">Toplam Alınan Ödeme</span>
          <span className="value">
            {(report.totalConstructionPayment||0).toLocaleString()} TL
          </span>
        </div>
        <div className="total-card remaining">
          <span className="label">Kalan Alacak</span>
          <span className="value">
            {(((report.totalContractPrice||0)
             + (report.totalServiceAmount||0)
             - (report.totalConstructionPayment||0))
             .toLocaleString())} TL
          </span>
        </div>
        <div className="total-card invoiced">
          <span className="label">Toplam Kesilen Fatura</span>
          <span className="value">
            {(report.totalConstructionPaymentInvoiceAmount||0)
             .toLocaleString()} TL
          </span>
        </div>
        <div className="total-card tobebilled">
          <span className="label">Kesilmesi Gereken Fatura</span>
          <span className="value">
            {(((report.totalContractPrice||0)
             + (report.totalServiceAmount||0)
             - (report.totalConstructionPaymentInvoiceAmount||0))
             .toLocaleString())} TL
          </span>
        </div>
      </div>

      {/* RENTALS & SERVICES */}
      <div className="table-container">
        <h2>Kiralama Raporu</h2>
        <table className="rentals-table">
          <thead>
            <tr>
              <th>Seri No</th>
              <th>Kiracı Firma</th>
              <th>Kira</th>
              <th>Başlangıç</th>
              <th>Bitiş</th>
              <th>Süre</th>
              <th>Toplam Hakediş</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {report.rentals.map((r,idx)=> {
              const days = r.extraPeriod ?? r.extratPeriod ?? 0;
              const rid  = r.rentalId;
              return (
                <React.Fragment key={idx}>
                  <tr>
                    <td>{r.serialNo}</td>
                    <td>{r.rentingCompany}</td>
                    <td>{(r.rent||0).toLocaleString()} TL</td>
                    <td>{new Date(r.startDate).toLocaleDateString()}</td>
                    <td>{new Date(r.endDate).toLocaleDateString()}</td>
                    <td>{formatDuration(r.rentalPeriod, r.extraPeriodMonth, days)}</td>
                    <td className="highlight">
                      {(r.contractPrice||0).toLocaleString()} TL
                    </td>
                    <td className="actions">
                      <FaPlus
                        className="icon add-service"
                        title="Servis Ekle"
                        onClick={()=>openServiceModal(rid)}
                      />
                      <FaEdit
                        className="icon edit-rental"
                        title="Kira Düzenle"
                        onClick={()=>navigate(`/rentaledit/${rid}`)}
                      />
                    </td>
                  </tr>

                  {(servicesByRental[rid]||[]).map(s=>(
                    <tr key={s.id} className="service-row">
                      <td>Servis Hizmeti</td>
                      <td>{s.description}</td>
                      <td className="highlight">{(s.amount||0).toLocaleString()} TL</td>
                      <td>{new Date(s.serviceDate).toLocaleDateString()}</td>
                      <td colSpan={3}>{s.note}</td>
                      <td className="actions">
                        <FaEdit
                          className="icon edit-service"
                          title="Servis Düzenle"
                          onClick={()=>openServiceModal(rid, s)}
                        />
                        <FaTrash
                          className="icon del-service"
                          title="Servisi Sil"
                          onClick={()=>deleteService(s.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAYMENTS */}
      <div className="table-container payments">
        <h2>Alınan Ödemeler</h2>
        <button className="btn add-btn" onClick={()=>openPaymentModal(null)}>
          <FaPlus /> Ödeme Ekle
        </button>
        <table className="payments-table">
          <thead>
            <tr>
              <th>Teslim Tarihi</th>
              <th>Çek/Senet Tarihi</th>
              <th>Çek/Senet No</th>
              <th>Tür</th>
              <th>Ödeme</th>
              <th>Fatura Tutarı</th>
              <th>Fatura Kesildi</th>
              <th>Fatura Tarihi</th>
              <th>Fatura No</th>
              <th className="center">İnşaat Ödemesi</th>
              <th>Not</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {constructionPayments.map(p=>(
              <tr key={p.id}>
                <td>{p.deliveryDate ? new Date(p.deliveryDate).toLocaleDateString() : '-'}</td>
                <td>{p.chequeOrBondDate ? new Date(p.chequeOrBondDate).toLocaleDateString() : '-'}</td>
                <td>{p.chequeOrBondNumber||'-'}</td>
                <td>{
                  p.paymentType==='Cheque'?'Çek'
                  :p.paymentType==='Bond'?'Senet'
                  :p.paymentType==='Transfer'?'Havale':'-'
                }</td>
                <td>{(p.amount||0).toLocaleString()} TL</td>
                <td>{(p.invoiceAmount||0).toLocaleString()} TL</td>
                <td>{p.isInvoiceIssued ? 'Evet' : 'Hayır'}</td>
                <td>{p.invoiceDate ? new Date(p.invoiceDate).toLocaleDateString() : '-'}</td>
                <td>{p.invoiceNumber}</td>
                <td className="center">{p.constructionPayment ? '✔️' : '-'}</td>
                <td>{p.note}</td>
                <td className="actions">
                  <FaEdit className="icon edit-payment"   onClick={()=>openPaymentModal(p)} />
                  <FaTrash className="icon del-payment"   onClick={()=>deletePayment(p.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAYMENT MODAL */}
      {showPaymentModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>{editingPaymentId ? 'Ödeme Düzenle' : 'Yeni Ödeme'}</h3>
            <div className="modal-body">
              {/* all fields */}
              <div className="field half">
                <label>Teslim Tarihi</label>
                <input
                  type="date"
                  name="DeliveryDate"
                  value={paymentForm.DeliveryDate}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Çek/Senet Tarihi</label>
                <input
                  type="date"
                  name="ChequeOrBondDate"
                  value={paymentForm.ChequeOrBondDate}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Çek/Senet No</label>
                <input
                  type="text"
                  name="ChequeOrBondNumber"
                  value={paymentForm.ChequeOrBondNumber}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Tür</label>
                <select
                  name="PaymentType"
                  value={paymentForm.PaymentType}
                  onChange={handlePaymentChange}
                >
                  <option value="Cheque">Çek</option>
                  <option value="Bond">Senet</option>
                  <option value="Transfer">Havale</option>
                </select>
              </div>
              <div className="field half">
                <label>Ödeme</label>
                <input
                  type="number"
                  name="Amount"
                  value={paymentForm.Amount}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Fatura Tutarı</label>
                <input
                  type="number"
                  name="InvoiceAmount"
                  value={paymentForm.InvoiceAmount}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Fatura Kesildi</label>
                <input
                  type="checkbox"
                  name="IsInvoiceIssued"
                  checked={paymentForm.IsInvoiceIssued}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Fatura Tarihi</label>
                <input
                  type="date"
                  name="InvoiceDate"
                  value={paymentForm.InvoiceDate}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>Fatura No</label>
                <input
                  type="text"
                  name="InvoiceNumber"
                  value={paymentForm.InvoiceNumber}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field half">
                <label>İnşaat Ödemesi</label>
                <input
                  type="checkbox"
                  name="ConstructionPayment"
                  checked={paymentForm.ConstructionPayment}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field full-width">
                <label>Not</label>
                <input
                  type="text"
                  name="Note"
                  value={paymentForm.Note}
                  onChange={handlePaymentChange}
                />
              </div>
              <div className="field full-width">
                <label>Dosya Ekle</label>
                <input
                  type="file"
                  multiple
                  onChange={handlePaymentFile}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={submitPayment}>Kaydet</button>
              <button className="btn cancel" onClick={closePaymentModal}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* SERVICE MODAL */}
      {showServiceModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>{editingServiceId ? 'Servis Düzenle' : 'Yeni Servis'}</h3>
            <div className="modal-body">
              <div className="field full-width">
                <label>Açıklama</label>
                <input
                  type="text"
                  name="Description"
                  value={serviceForm.Description}
                  onChange={handleServiceChange}
                />
              </div>
              <div className="field half">
                <label>Servis Tarihi</label>
                <input
                  type="date"
                  name="ServiceDate"
                  value={serviceForm.ServiceDate}
                  onChange={handleServiceChange}
                />
              </div>
              <div className="field half">
                <label>Bedel</label>
                <input
                  type="number"
                  name="Amount"
                  value={serviceForm.Amount}
                  onChange={handleServiceChange}
                />
              </div>
              <div className="field full-width">
                <label>Not</label>
                <input
                  type="text"
                  name="Note"
                  value={serviceForm.Note}
                  onChange={handleServiceChange}
                />
              </div>
              <div className="field full-width">
                <label>Dosya Ekle</label>
                <input
                  type="file"
                  multiple
                  onChange={handleServiceFile}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={submitService}>Kaydet</button>
              <button className="btn cancel" onClick={closeServiceModal}>İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
